import React, { useState, useMemo } from "react";
import {
  AttendanceHeader,
  VetStepArea,
  DataCollectionStep,
  PrescriptionsStep,
  SignatureStep,
  StepNavigationButtons,
} from "./components";
import { LargeContentBox } from "@/components/common";
import {
  Button,
  Stack,
  CircularProgress,
  Box,
  Typography,
  Dialog,
  Slide,
  Fab,
  Tooltip,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { DecomposerResultViewer } from "../decomposerviewer/DecomposerResultViewer";
import { Description, Pets, Videocam } from "@mui/icons-material";
import { PageHeader } from "@/components/common/PageHeader";
import {
  VetAttendanceProvider,
  useVetAttendance,
} from "@/fluxvet/contexts/VetAttendanceContext";
import {
  generateSectionDocument,
  DocumentGenerationResult,
} from "@/fluxvet/services/documentGenerator";
import { convertToQuestionnaireResponse } from "@/fluxvet/models/questionnaire-mapper";
import { PostQuestionnaireResponse } from "@/services/fhir/patientsummary/questionnaireResponse/PostQuestionnaireResponse";
import { PostMedicationRequest } from "@/services/fhir/patientsummary/medicationrequest/PostMedicationRequest";
import { PostDocumentReference } from "@/services/fhir/patientsummary/documentreference/PostDocumentReference";
import { PostServiceRequest } from "@/services/fhir/patientsummary/serviceRequest/PostServiceRequest";
import dayjs from "dayjs";
import { endpoints, oids } from "@/configs/Settings";
import { useSessionContext } from "@/contexts/SessionContext";
import { useLocation, useNavigate } from "react-router-dom";
import { usePractitionerRoleContext } from "@/contexts/PractitionerRoleContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useFhirData } from "@/utils/useFhirData";
import { PatchEncounterStatus } from "@/services/fhir/encounter/PatchEncounterStatus";
import { DeleteWhereby } from "@/services/whereby/DeleteWhereby";
import { PatchAppointmentStatus } from "@/services/fhir/appointment/PatchAppointmentStatus";
import { toast } from "react-toastify";
import { useVetDocumentInfo } from "@/fluxvet/hooks/useVetDocumentInfo";
import { PutQuestionnaireResponse } from "@/services/fhir/patientsummary/questionnaireResponse/PutQuestionnaireResponse";
import { GetAdminQuestionnaireResponse } from "@/services/fhir/questionnaireresponse/GetAdminQuestionnaireResponse";
import { EHRRecordsDialog } from "@/pages/ehrrecords/EHRRecordsDialog";
import type { QuestionnairePayload } from "src-next/modules/questionnaire-builder/form-builder/types";
import { useQuestionnaireManagement } from "src-next/modules/questionnaire-builder/questionnaire-management/hooks";
import { useQuestionnaireTaskSend } from "src-next/modules/questionnaire-builder/questionnaire-tasks/hooks/useQuestionnaireTaskSend";
import { resolveOptionalAppointmentBasedOn } from "src-next/modules/questionnaire-builder/questionnaire-tasks";
import { isQuestionnaireSelectableForClinicalUse } from "@/utils/questionnaireClinicalSelection";

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// =============================================================================
// FUNÇÕES AUXILIARES - Customização de documentos (mesmo padrão do SignatureStep)
// =============================================================================

/**
 * Normaliza o base64 da imagem de background para o formato esperado pelo VetDocumentTemplate.
 * Remove o prefixo "data:image/...;base64," se existir.
 *
 * @param value - Base64 bruto ou data URL
 * @returns Base64 puro ou undefined se inválido
 */
const normalizeBackgroundBase64 = (
  value: string | undefined,
): string | undefined => {
  if (!value || typeof value !== "string") return undefined;
  const match = value.match(/^data:image\/\w+;base64,(.+)$/);
  return match ? match[1] : value;
};

/**
 * Busca a customização de background do PDF baseado no tipo de documento
 * Mapeia os tipos do sistema veterinário para os tipos de configuração:
 * - "treatment" -> "simple" (receituário simples)
 * - "exam" -> "exam" (solicitação de exame)
 * - "report" -> "certificate" (relatório/atestado)
 *
 * @param type - Tipo do documento veterinário ("treatment" | "exam" | "report")
 * @param organizationId - ID da organização
 * @param username - Username do usuário
 * @returns Promise com objeto contendo backgroundImage ou undefined se não encontrado
 */
const getCustomization = async (
  type: "treatment" | "exam" | "report",
  organizationId: string | undefined,
  username: string | undefined,
) => {
  const response = await GetAdminQuestionnaireResponse({
    questionnaire: "customPdf",
    author: organizationId,
    source: `Practitioner/${oids.cpf}-${username}`,
  });

  // Mapeia o tipo do documento veterinário para o tipo de configuração
  const configTypeMap: Record<"treatment" | "exam" | "report", string> = {
    treatment: "simple", // Receituário simples
    exam: "exam", // Solicitação de exame
    report: "certificate", // Relatório/atestado
  };

  const configType = configTypeMap[type];

  // Filtra o backgroundImage baseado no tipo de configuração
  const backgroundImage =
    response?.entry?.filter(
      (el: any) =>
        el.resource?.item?.[0]?.answer?.[0]?.valueString === configType,
    )?.[0]?.resource?.item?.[2]?.answer?.[0]?.valueAttachment || undefined;

  return { backgroundImage };
};

// =============================================================================
// TIPOS - Estrutura FHIR dos dados
// =============================================================================

interface FHIRPatientPet {
  resourceType: string;
  id: string;
  extension?: Array<{
    url: string;
    extension?: Array<{
      url: string;
      valueCodeableConcept?: {
        coding: Array<{ system: string; code: string; display: string }>;
      };
    }>;
  }>;
  identifier?: Array<{ system: string; value: string }>;
  name?: Array<{ use: string; text: string }>;
  gender?: string;
  birthDate?: string;
  photo?: Array<{ contentType: string; data: string }>;
}

interface FHIRPatientTutor {
  resourceType: string;
  id: string;
  identifier?: Array<{
    type?: { coding: Array<{ system: string; code: string }> };
    system: string;
    value: string;
  }>;
  name?: Array<{ use: string; text: string }>;
  telecom?: Array<{ system: string; value: string }>;
  gender?: string;
  birthDate?: string;
}

// =============================================================================
// FUNÇÕES AUXILIARES - Adaptação de dados
// =============================================================================

/**
 * Adapta tratamentos veterinários para o formato esperado pelo MedicationRequest
 * Converte a estrutura simples do formulário veterinário para o formato médico
 *
 * @param treatments - Array de tratamentos do formulário veterinário
 * @returns Array no formato esperado pelo PostMedicationRequest
 */
const adaptTreatmentsToMedicationFormat = (treatments: any[]) => {
  return treatments.map((treatment) => ({
    name: treatment.name, // Nome do medicamento
    note: `${treatment.dosage}${
      treatment.description ? `\n${treatment.description}` : ""
    }`, // Combina dosagem e descrição
    // Campos estruturados podem ser adicionados futuramente se necessário
    // frequency: null,
    // period: null,
    // quantity: null,
  }));
};

/**
 * Adapta exames/procedimentos veterinários para o formato ServiceRequest
 *
 * @param examsOrProcedures - Array de exames do formulário veterinário
 * @returns Array no formato esperado pelo PostServiceRequest
 */
const adaptExamsToServiceRequestFormat = (examsOrProcedures: any[]) => {
  return examsOrProcedures.map((exam) => ({
    name: exam.description,
    note: exam.notes,
    // category pode ser adicionado futuramente
  }));
};

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Componente interno com acesso ao contexto
 */
const VetAttendenceContent: React.FC = () => {
  const { user, access } = useSessionContext();
  const navigate = useNavigate();
  const { userPractitioner, userPractitionerRole, userSpecialties } =
    usePractitionerRoleContext();
  const { organization, organizationId } = useOrganizationContext();

  const { sendQuestionnaireTasks } = useQuestionnaireTaskSend();
  const { questionnaires: orgQuestionnairesForSendGuard } =
    useQuestionnaireManagement({
      // Mesmo padrão do `QuestionnairesTabPanel`: id numérico; o serviço aplica o prefixo OID.
      organizationId: organizationId || "",
    });

  const questionnaireByIdForClinicalSend = useMemo(() => {
    const m = new Map<string, QuestionnairePayload>();
    for (const q of orgQuestionnairesForSendGuard) {
      if (q.id) m.set(q.id.trim(), q);
    }
    return m;
  }, [orgQuestionnairesForSendGuard]);

  const practitionerRoleReference = useMemo(() => {
    const username = user?.username;
    if (!access?.organizationId || typeof username !== "string") return "";
    return `PractitionerRole/${access.organizationId}-${oids.cpf}-${username}`;
  }, [access?.organizationId, user?.username]);

  const {
    methods,
    loadingQuestionnaireResponse,
    appointment,
    petData,
    tutorData,
    locationAttendanceChannel,
    questionnaireDefualt,
  } = useVetAttendance();

  const patientReferenceForQuestionnaireTasks = useMemo(() => {
    const id = petData?.id;
    return id ? `Patient/${id}` : null;
  }, [petData?.id]);

  const appointmentReferenceForQuestionnaireTasks = useMemo(
    () => resolveOptionalAppointmentBasedOn(appointment?.id),
    [appointment?.id],
  );

  const { state } = useLocation();
  const references = state?.encounterReference;

  // Hook customizado para centralizar a lógica de informações do documento
  const { petInfo, tutorInfo, clinicInfo } = useVetDocumentInfo();

  const healthcare = useFhirData?.findParticipant(
    appointment?.participant,
    "HealthcareService"
  )?.display;

  const servicePratice = userSpecialties?.find(
    (el: any) => el.coding?.[0]?.display === healthcare
  );

  const docReferenceData = {
    organization: {
      id: organization?.id,
      alias: organization?.alias?.[0],
      name: organization?.name,
    },
    practitioner: {
      id: userPractitioner?.id,
      name: userPractitioner?.name?.[0]?.text,
    },
    servicePratice: {
      display: servicePratice?.coding?.[0]?.display || healthcare,
      code: servicePratice?.coding?.[0]?.code || healthcare,
      system: "https://fluxmed.com.br/fhir/CodeSystem/CBO",
    },
    time: {
      start: appointment?.start,
      end: appointment?.end,
    },
  };

  // Estado para controlar a etapa ativa do atendimento (0, 1 ou 2)
  const [activeStep, setActiveStep] = useState<number>(0);
  // Estado para controlar o loading durante a finalização
  const [isFinishing, setIsFinishing] = useState<boolean>(false);
  // Estado para armazenar o resultado do decomposer (visualização temporária)
  const [decomposerResult, setDecomposerResult] = useState<any>(null);

  // Total de etapas
  const totalSteps = 3;

  /**
   * Avança para a próxima etapa
   */
  const handleNext = () => {
    if (activeStep < totalSteps - 1) {
      setActiveStep((prev) => prev + 1);
    }
  };

  /**
   * Volta para a etapa anterior
   */
  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prev) => prev - 1);
    }
  };

  /**
   * Finaliza o atendimento (última etapa)
   * Persiste recursos FHIR (MedicationRequest, ServiceRequest, DocumentReference)
   * Gera documentos para itens não assinados e exibe resumo completo
   * Gera QuestionnaireResponse FHIR para futura persistência
   */
  const handleFinish = async () => {
    // Previne múltiplos cliques durante a finalização
    if (isFinishing) {
      return;
    }

    // Ativa o estado de loading
    setIsFinishing(true);

    try {
      // Busca todos os dados do formulário
      const formData = methods.getValues();
      const treatments = formData.treatments || [];
      const examsOrProcedures = formData.examsOrProcedures || [];
      const reports = formData.reports || [];
      const signedDocuments = formData.signedDocuments || [];

      // Filtra itens NÃO assinados
      // (itens assinados já foram persistidos na etapa de assinatura digital)
      const unsignedTreatments = treatments.filter((t: any) => !t.signed);
      const unsignedExams = examsOrProcedures.filter((e: any) => !e.signed);
      const unsignedReports = reports.filter((r: any) => !r.signed);

      const unsignedDocuments: Array<{
        title: string;
        type: string;
        items: any[];
        document?: DocumentGenerationResult;
      }> = [];

      // =====================================================================
      // PERSISTÊNCIA DE MEDICATIONREQUEST (Tratamentos)
      // =====================================================================
      let medicationBasedOnRefs = "";

      if (unsignedTreatments.length > 0) {
        // Adapta tratamentos veterinários para formato MedicationRequest
        const adaptedTreatments =
          adaptTreatmentsToMedicationFormat(unsignedTreatments);

        // Cria os MedicationRequest no servidor FHIR

        try {
          const medicationResponse = await PostMedicationRequest(
            adaptedTreatments,
            references
          );

          // Extrai referências dos MedicationRequests criados para vincular ao DocumentReference
          medicationBasedOnRefs =
            medicationResponse?.entry
              ?.map(
                (entry: any) =>
                  `<fhir:basedOn>
                  <fhir:reference value="${
                    entry.response?.location?.split("/_history")?.[0]
                  }"/>
                </fhir:basedOn>`
              )
              ?.join("") || "";

          if (unsignedTreatments.length > 0 && medicationBasedOnRefs) {
            // Busca customização específica para tratamentos
            const customization = await getCustomization(
              "treatment",
              organization?.id,
              user?.username,
            );
            const att = customization?.backgroundImage;
            const hasBackground = att?.data;
            const additionalInfo = {
              ...clinicInfo,
              ...(hasBackground && {
                backgroundImage: normalizeBackgroundBase64(att.data),
                backgroundImageContentType: att.contentType || "image/png",
              }),
            };

            // Gera o documento PDF para os tratamentos não assinados (com customização de background)
            const treatmentDoc = await generateSectionDocument(
              "treatment",
              unsignedTreatments,
              petInfo,
              tutorInfo,
              additionalInfo
            );

            if (treatmentDoc?.base64) {
              await PostDocumentReference(
                {
                  ...docReferenceData,
                  base64: treatmentDoc.base64,
                  codeValue: "REC_SIM",
                  codeDisplay: "Receituário Simples",
                  categoryValue: "prescricao",
                  categoryDisplay: "Receituário",
                },
                references,
                medicationBasedOnRefs
              );
            }
          }
        } catch (error) {
          toast.warn("Não foi possível registrar os tratamentos.");
        }
      }

      // =====================================================================
      // GERAÇÃO DE PDF E PERSISTÊNCIA DE DOCUMENTREFERENCE (Tratamentos NÃO Assinados)
      // =====================================================================

      // =====================================================================
      // PERSISTÊNCIA DE SERVICEREQUEST (Exames/Procedimentos)
      // =====================================================================
      let serviceBasedOnRefs = "";

      if (unsignedExams.length > 0) {
        try {
          // Adapta exames veterinários para formato ServiceRequest
          const adaptedExams = adaptExamsToServiceRequestFormat(unsignedExams);

          // Cria os ServiceRequest no servidor FHIR
          const serviceResponse = await PostServiceRequest(
            adaptedExams,
            references,
            [] // contained (vazio por enquanto)
          );

          // Extrai referências dos ServiceRequests criados
          serviceBasedOnRefs =
            serviceResponse?.entry
              ?.map(
                (entry: any) =>
                  `<fhir:basedOn>
                  <fhir:reference value="${
                    entry.response?.location?.split("/_history")?.[0]
                  }"/>
                </fhir:basedOn>`
              )
              ?.join("") || "";

          // =====================================================================
          // GERAÇÃO DE PDF E PERSISTÊNCIA DE DOCUMENTREFERENCE (Exames NÃO Assinados)
          // =====================================================================
          if (unsignedExams.length > 0 && serviceBasedOnRefs) {
            // Busca customização específica para exames
            const customization = await getCustomization(
              "exam",
              organization?.id,
              user?.username,
            );
            const att = customization?.backgroundImage;
            const hasBackground = att?.data;
            const additionalInfo = {
              ...clinicInfo,
              ...(hasBackground && {
                backgroundImage: normalizeBackgroundBase64(att.data),
                backgroundImageContentType: att.contentType || "image/png",
              }),
            };

            // Gera o documento PDF para os exames não assinados (com customização de background)
            const examDoc = await generateSectionDocument(
              "exam",
              unsignedExams,
              petInfo,
              tutorInfo,
              additionalInfo
            );

            if (examDoc?.base64) {
              await PostDocumentReference(
                {
                  ...docReferenceData,
                  base64: examDoc.base64,
                  codeValue: "SERVICE_REQUEST",
                  codeDisplay: "Solicitação de exame",
                  categoryValue: "service-request",
                  categoryDisplay: "Solicitação",
                },
                references,
                serviceBasedOnRefs
              );
            }
          }
        } catch (error) {
          toast.warn("Não foi possível registrar os exames/procedimentos.");
        }
      }

      // =====================================================================
      // GERAÇÃO DE PDF E PERSISTÊNCIA DE DOCUMENTREFERENCE (Relatórios NÃO Assinados)
      // =====================================================================
      // Relatórios não têm MedicationRequest/ServiceRequest, vão direto como DocumentReference
      if (unsignedReports.length > 0) {
        try {
          // Busca customização específica para relatórios
          const customization = await getCustomization(
            "report",
            organization?.id,
            user?.username,
          );
          const att = customization?.backgroundImage;
          const hasBackground = att?.data;
          const additionalInfo = {
            ...clinicInfo,
            ...(hasBackground && {
              backgroundImage: normalizeBackgroundBase64(att.data),
              backgroundImageContentType: att.contentType || "image/png",
            }),
          };

          // Gera o documento PDF para os relatórios não assinados (com customização de background)
          const reportDoc = await generateSectionDocument(
            "report",
            unsignedReports,
            petInfo,
            tutorInfo,
            additionalInfo
          );

          if (reportDoc?.base64) {
            await PostDocumentReference(
              {
                ...docReferenceData,
                base64: reportDoc.base64,
                codeValue: "AT",
                codeDisplay: "Atestado",
                categoryValue: "atestado",
                categoryDisplay: "Relatório",
              },
              references,
              ""
            );
          }
        } catch (error) {
          toast.warn("Não foi possível registrar os relatórios.");
        }
      }

      // =====================================================================
      // PERSISTÊNCIA DE QUESTIONNAIRERESPONSE (Todos os dados do atendimento)
      // =====================================================================
      try {
        // Garante que petData existe (já verificado antes de renderizar)
        // Usa o ID do pet ou fallback para o subject do encounterReference
        // Como já verificamos que petData existe, podemos usar com segurança
        const petId = petData?.id || references?.subject || "";

        if (!petId) {
          throw new Error("ID do paciente não encontrado");
        }

        const questionnaireResponse = convertToQuestionnaireResponse(
          formData,
          petId, // ID do paciente (pet)
          `Practitioner/${oids.cpf}-${user?.username}`,
          user?.name,
          references?.id
        );

        const result = questionnaireDefualt?.id
          ? await PutQuestionnaireResponse(
              {
                ...questionnaireResponse,
                id: questionnaireDefualt?.id,
                extension: questionnaireDefualt?.extension,
              },
              questionnaireDefualt?.id,
              { decomposer: true }
            )
          : await PostQuestionnaireResponse(
              {
                ...questionnaireResponse,
                questionnaire: `Questionnaire/${endpoints.QUESTIONNARIE_ID}`,
              },
              { decomposer: true }
            );

        // ---------------------------------------------------------------------
        // Envio de questionários ao tutor (tasks) — paridade com Footer do humano.
        // Falha aqui não impede PatchAppointmentStatus nem navegação.
        // ---------------------------------------------------------------------
        const questionnaireInterventions = formData.selectedQuestionnaires ?? [];
        const canSendQuestionnaireTasks =
          questionnaireInterventions.length > 0 &&
          !!patientReferenceForQuestionnaireTasks &&
          !!organizationId &&
          !!practitionerRoleReference &&
          access?.type === "professional";

        if (canSendQuestionnaireTasks) {
          const byId = new Map<
            string,
            (typeof questionnaireInterventions)[number]
          >();
          for (const item of questionnaireInterventions) {
            const qid =
              typeof item?.questionnaireId === "string"
                ? item.questionnaireId.trim()
                : "";
            if (qid && !byId.has(qid)) byId.set(qid, item);
          }

          const usageOk = (
            u: unknown,
          ): u is QuestionnairePayload["usageType"] =>
            u === "triage" ||
            u === "preConsultation" ||
            u === "postConsultation" ||
            u === "attendance";

          let skippedDraftQuestionnaireCount = 0;
          const questionnairesPayload: QuestionnairePayload[] = [];

          for (const item of byId.values()) {
            const id =
              typeof item?.questionnaireId === "string"
                ? item.questionnaireId.trim()
                : "";
            if (!id) continue;

            const full = questionnaireByIdForClinicalSend.get(id);
            if (!full) {
              console.warn(
                "[VetAttendence] Questionário não encontrado na lista da organização; envio ignorado:",
                id,
              );
              continue;
            }
            if (!isQuestionnaireSelectableForClinicalUse(full)) {
              skippedDraftQuestionnaireCount += 1;
              continue;
            }

            const usageType = usageOk(item?.usageType)
              ? item.usageType
              : usageOk(full.usageType)
                ? full.usageType
                : "postConsultation";

            questionnairesPayload.push({
              id,
              title:
                full.title || item?.title || item?.name || "Questionário",
              description: full.description ?? "",
              usageType,
              questions: [] as QuestionnairePayload["questions"],
            });
          }

          if (skippedDraftQuestionnaireCount > 0) {
            toast.info(
              skippedDraftQuestionnaireCount === 1
                ? "1 questionário em rascunho não foi enviado ao tutor."
                : `${skippedDraftQuestionnaireCount} questionários em rascunho não foram enviados ao tutor.`,
            );
          }

          if (questionnairesPayload.length) {
            try {
              await sendQuestionnaireTasks({
                organizationId,
                practitionerRoleReference,
                patientReference: patientReferenceForQuestionnaireTasks,
                ...(appointmentReferenceForQuestionnaireTasks
                  ? {
                      appointmentReference:
                        appointmentReferenceForQuestionnaireTasks,
                    }
                  : {}),
                questionnaires: questionnairesPayload,
              });
              toast.success(
                `${questionnairesPayload.length} questionário(s) enviado(s) ao tutor.`,
              );
            } catch (questionnaireSendErr) {
              console.error(
                "Erro ao enviar questionários (tasks) ao finalizar atendimento vet:",
                questionnaireSendErr,
              );
              toast.warning(
                "Atendimento salvo, mas alguns questionários não puderam ser enviados ao tutor.",
              );
            }
          }
        }

        await PatchAppointmentStatus({
          appointmentId: references?.appointmentId,
          status: "fulfilled",
        });

        await DeleteWhereby(state?.encounterReference?.sessionKey);
        
        // Usa o resultado do QuestionnaireResponse (API unificada)
        setDecomposerResult(result);
        
        toast.success("Atendimento registrado e enviado para processamento!");
        
        // Comentado para permitir visualização do resultado antes de sair
        navigate("/");
      } catch (error) {
        console.error("❌ Erro ao finalizar atendimento:", error);
        toast.error("Erro ao finalizar atendimento. Tente novamente.");
        // Não re-lança o erro para evitar mensagens duplicadas
        // O catch externo já vai capturar e tratar adequadamente
      }
    } catch (error) {
      console.error("❌ Erro ao finalizar atendimento:", error);
      toast.error("Erro ao finalizar atendimento. Tente novamente.");
    } finally {
      // Desativa o estado de loading independente de sucesso ou erro
      setIsFinishing(false);
    }
  };

  /**
   * Renderiza o conteúdo da etapa ativa
   */
  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return <DataCollectionStep />;
      case 1:
        return <PrescriptionsStep />;
      case 2:
        return <SignatureStep />;
      default:
        return <DataCollectionStep />;
    }
  };

  // Verifica se está carregando dados ou se os dados essenciais não estão disponíveis
  // Só renderiza o conteúdo quando tudo estiver carregado e quando houver dados
  const isLoading = loadingQuestionnaireResponse;
  const hasEssentialData = appointment && petData && tutorData;

  // Exibe tela de carregamento enquanto busca dados ou quando dados essenciais não estão disponíveis
  if (isLoading || !hasEssentialData) {
    return (
      <LargeContentBox>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight="400px"
          gap={2}
        >
          <CircularProgress size={60} />
          <Typography variant="h6" color="textSecondary">
            {isLoading
              ? "Carregando dados do atendimento..."
              : "Aguardando dados do atendimento..."}
          </Typography>
        </Box>
      </LargeContentBox>
    );
  }

  // Abaixo segue o dialog com o viewer do decomposer em prod não tem
  /*
      <Dialog
        fullScreen
        open={!!decomposerResult}
        onClose={() => {
          setDecomposerResult(null);
          navigate("/");
        }}
        TransitionComponent={Transition}
      >
        {decomposerResult && (
          <DecomposerResultViewer 
            bundleResult={decomposerResult} 
            onClose={() => {
              setDecomposerResult(null);
              navigate("/");
            }} 
          />
        )}
      </Dialog>
      */
  return (
    <LargeContentBox>
      
   
      <Stack width="100%" spacing={2}>
        {/* <PageHeader
          title="Atendimento veterinário"
          icon={Pets}
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<Videocam />}
            >
              <Videocam />
              Teleconsulta
            </Button>
          }
        /> */}
        {/* Cabeçalho com informações do Pet e Tutor */}
        <AttendanceHeader
          pet={petData as FHIRPatientPet}
          tutor={tutorData as FHIRPatientTutor}
          appointment={appointment}
          locationAttendanceChannel={locationAttendanceChannel}
        />

        {/* Navegação por etapas (Cards) */}
        <VetStepArea activeStep={activeStep} setActiveStep={setActiveStep} />

        {/* Conteúdo da etapa ativa */}
        {renderStepContent()}

        {/* Botões de navegação Voltar/Próximo */}
        <StepNavigationButtons
          activeStep={activeStep}
          totalSteps={totalSteps}
          onBack={handleBack}
          onNext={handleNext}
          onFinish={handleFinish}
          isFinishing={isFinishing}
        />
      </Stack>
    </LargeContentBox>
  );
};

/**
 * Componente principal da tela de atendimento veterinário
 * Gerencia as 3 etapas do atendimento:
 * 1. Coleta de dados (Atendimento)
 * 2. Prescrições
 * 3. Assinatura de documentos
 *
 * Suporta dois modos:
 * - Novo atendimento: initialData e attendanceId = undefined
 * - Editar atendimento: initialData com dados existentes + attendanceId
 */
const VetAttendence: React.FC = () => {
  // ===== EXEMPLO DE DADOS PARA EDIÇÃO =====
  // TODO: Buscar da API baseado no attendanceId da rota/props
  // const attendanceId = useParams().id;
  // const { data: attendanceData } = useQuery(['attendance', attendanceId]);

  // Dados simulados para exemplo de edição (descomente para testar)
  // const initialAttendanceData = {
  //   mainComplaint: "Pet apresentou vômitos há 2 dias",
  //   anamnesis: "Histórico de sensibilidade alimentar",
  //   physicalExam: "Estado geral bom, mucosas normocoradas",
  //   temperature: 38.5,
  //   heartRate: 120,
  //   weight: 15.2,
  // };
  // const editingAttendanceId = "attendance-123";

  // Para novo atendimento, deixe undefined
  const initialAttendanceData = undefined;
  const editingAttendanceId = undefined;

  const { state } = useLocation();
  const [openSummary, setOpenSummary] = useState<boolean>(false);

  const handleCloseSummary = () => {
    setOpenSummary(false);
  };

  return (
    <VetAttendanceProvider
      initialData={initialAttendanceData}
      attendanceId={editingAttendanceId}
    >
      <VetAttendenceContent />

      <Fab
        color="primary"
        aria-label="add"
        onClick={() => setOpenSummary(true)}
        sx={{
          position: "fixed",
          bottom: { xs: 90, md: 100 },
          right: { xs: 5, md: 40 },
          bgcolor: "#00A884",
          "&:hover": { bgcolor: "#008c6e" },
        }}
      >
        <Tooltip title="Sumário de saúde">
          <Description />
        </Tooltip>
      </Fab>

      {state?.encounterReference ? (
        <>
          <EHRRecordsDialog
            open={openSummary}
            handleClose={handleCloseSummary}
            references={state?.encounterReference}
          />
        </>
      ) : (
        <></>
      )}
    </VetAttendanceProvider>
  );
};

export default VetAttendence;
